import log from 'loglevel';
import { Color } from '../../styles/Color';

type Cleanup = () => void;

export function createFlexMicroform({
	jwt,
	clientLibrary,
	clientLibraryIntegrity,
	onError,
	onLoad,
}: {
	jwt: string;
	clientLibrary: string;
	clientLibraryIntegrity: string;
	onError: (error: Error) => void;
	onLoad: (microform: IMicroform) => void;
}): Cleanup {
	const script = document.createElement('script');
	script.src = clientLibrary;
	script.integrity = clientLibraryIntegrity;
	script.crossOrigin = 'anonymous';
	script.async = true;

	document.body.appendChild(script);

	let flexMicroform: Flex | undefined;

	script.onload = async () => {
		log.debug('Cybersource Microform script loaded');

		if (!window.Flex) {
			onError(new Error('Can not find microform flex object on window'));
			return;
		}

		flexMicroform = new window.Flex(jwt);

		if (!flexMicroform.microform) {
			onError(new Error('Can not find microform flex object on window'));
			return;
		}

		const myStyles = {
			input: {
				'font-size': '16px',
				'font-family': 'Avenir-Pro,Avenir,sans-serif',
			},
			invalid: { color: `${Color.ErrorDark}` },
		};
		const microform = flexMicroform.microform({ styles: myStyles });

		onLoad(microform);
	};

	script.onerror = () => {
		onError(new Error(`Can not load flex microform script: ${script.src}`));
	};

	return () => {
		if (flexMicroform?.microform) {
			flexMicroform.microform = undefined;
		}
		if ('Flex' in window) {
			delete window.Flex;
		}
		document.body.removeChild(script);
	};
}

export type Flex = {
	microform?: (options: { styles: unknown }) => IMicroform;
};

type FieldEventType =
	| 'autocomplete'
	| 'blur'
	| 'change'
	| 'error'
	| 'focus'
	| 'inputSubmitRequest'
	| 'load'
	| 'unload'
	| 'update';

interface IFieldEvent {
	valid: boolean;
	card?: [
		{
			name: string;
		},
	];
}

export interface IMicroform {
	createField: (
		type: 'number' | 'securityCode',
		options?: {
			placeholder?: string;
		},
	) => {
		load: (selector: string) => void;
		unload: () => void;
		dispose: () => void;
		on: (event: FieldEventType, callback: (event: IFieldEvent) => void) => void;
	};
	createToken: (
		options: { expirationMonth: string; expirationYear: string },
		callback: (err: Error | undefined, token: string) => void,
	) => string;
}

declare global {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	interface Window {
		Flex?: new (captureContext: string) => Flex;
	}
}
